import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SectionComponent } from '../../components/section/section.component';
import { imageToPicture } from '../../../app/components/plain-image/plain-image.component';
import { AspectRatio, RouterOrExternalLinkDirective, TemplateTypeDirective } from '@knorr-bremse-portals/ngx-components';
import { TeaserCardSection } from '../../../app/components/teaser-card/teaser-card.component';
import { CommonModule } from '@angular/common';
import { EditorIdDirective } from '../../../app/directives/editor-id.directive';
import { OgImageDirective } from '../../directives/og-image.directive';
import { Section, SectionAttributes } from '../../../app/types/section';
import { CardComponent } from '@knorr-bremse-portals/ngx-components/components/card';
import { StandaloneLinkComponent } from '@knorr-bremse-portals/ngx-components/components/standalone-link';
import { PictureComponent } from '@knorr-bremse-portals/ngx-components/components/picture';
import { OverlayFigureComponent } from '@knorr-bremse-portals/ngx-components/components/overlay-figure';
import { iconArrowsRight } from '@knorr-bremse-portals/icons';
import { FsButtonComponent } from '../../components/fs-button/fs-button.component';

type TeaserCardCollectionSection = Section<
  {
    numColumns: 4 | 8 | 12;
    items: TeaserCardSection[];
    headline: string;
    headlineLevel?: 'h1';
    secondHeadline: string;
    firstWide: boolean;
    reverseItems: boolean;
  } & SectionAttributes,
  [
    'items',
    'headline',
    'secondHeadline',
    'firstWide',
    'reverseItems',
    'noMarginBottom',
  ],
  'teaser-card-collection'
>;

@Component({
    selector: 'section[app-teaser-card-collection]',
    styleUrls: ['./teaser-card-collection.component.scss'],
    templateUrl: './teaser-card-collection.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule, EditorIdDirective,
        OgImageDirective,
        CardComponent,
        StandaloneLinkComponent,
        RouterOrExternalLinkDirective,
        PictureComponent,
        OverlayFigureComponent,
        TemplateTypeDirective,
        FsButtonComponent,
    ],
})
export class TeaserCardCollectionComponent extends SectionComponent<TeaserCardCollectionSection> implements OnInit {

  protected imageToPicture = imageToPicture;
  protected standardAspectRatio = AspectRatio.Standard;
  protected ultrawideAspectRatio = AspectRatio.Ultrawide;
  protected teaserTemplateType!: {
    card: TeaserCardSection;
    index: number;
  }

  protected iconLink = iconArrowsRight;

  protected displayModeFull(index: number): boolean {
    const numTeasers = this.attributes.items.length;
    const rootColumns = this.attributes.numColumns;
    if (rootColumns == 12 && numTeasers > 2 && this.attributes.firstWide && index !== 0) {
      return false;
    }
    return true;
  }

  protected getTeaserColumns(index: number): number {
    const numTeasers = this.attributes.items.length;
    if (numTeasers === 1) {
      return 12;
    }
    if (numTeasers === 2) {
      if (this.attributes.firstWide) {
        return index === 0 ? (this.attributes.secondHeadline ? 12 : 8) : (this.attributes.secondHeadline ? 12 : 4);
      }
      return 6;
    }
    if (this.attributes.firstWide) {
      return index === 0 ? (this.attributes.secondHeadline ? 12 : 8) : (this.attributes.secondHeadline ? 12 : 4);
    }

    return 4;
  }

  protected getRootTeaserColumns(index: number): number {
    const numTeasers = this.attributes.items.length;
    if (numTeasers === 1) {
      return this.attributes.numColumns;
    }
    if (numTeasers === 2) {
      if (this.attributes.firstWide) {
        return index === 0 ? (this.attributes.numColumns / 3)*2 : this.attributes.numColumns / 3;
      }
      return this.attributes.numColumns / 2;
    }
    if (this.attributes.firstWide) {
      return index === 0 ? (this.attributes.numColumns / 3)*2 : this.attributes.numColumns / 3;
    }

    return this.attributes.numColumns / 3;
  }

  protected getPictureAspectRatio(index: number): AspectRatio {
    const numTeasers = this.attributes.items.length;
    const rootColumns = this.attributes.numColumns;
    if (numTeasers === 1) {
      if (rootColumns === 4) {
        return AspectRatio.Standard;
      } else {
        return AspectRatio.Ultrawide;
      }
    }

    if (this.attributes.firstWide) {
      return index === 0 ? AspectRatio.Ultrawide : AspectRatio.Standard;
    }

    return AspectRatio.Standard;
  }

}
